import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { ApiError, apiFetch, hasLiveApi } from '@/api/client'
import {
  emptyTransferStatement,
  transferStatementFromEnvelope,
  type TransferStatementResult,
} from '@/api/map/transferStatement'

export type TransferStatementQuery = {
  from: string
  to: string
  page?: number
  pageSize?: number
  enabled?: boolean
  /** Bump to re-run Search. */
  run?: number
}

async function load(query: TransferStatementQuery): Promise<TransferStatementResult> {
  const page = Math.max(1, query.page ?? 1)
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  if (!query.from || !query.to) return emptyTransferStatement(page, pageSize)
  if (!hasLiveApi()) return emptyTransferStatement(page, pageSize)

  try {
    const envelope = await apiFetch<unknown>('/v1/transfer-statement/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        from: query.from.slice(0, 10),
        to: query.to.slice(0, 10),
        page,
        size: pageSize,
      }),
    })
    return transferStatementFromEnvelope(envelope, page, pageSize)
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 502 || err.status === 503)) {
      return emptyTransferStatement(page, pageSize)
    }
    throw err
  }
}

export function useTransferStatement(query: TransferStatementQuery) {
  return useQuery({
    queryKey: ['agent', 'transfer-statement', query],
    queryFn: () => load(query),
    enabled: Boolean(query.from && query.to && (query.enabled ?? true)),
    placeholderData: (prev) => prev,
  })
}

/** Exported for unit tests + Generate CSV. */
export async function loadTransferStatementAll(
  from: string,
  to: string,
): Promise<TransferStatementResult> {
  return load({ from, to, page: 1, pageSize: 10_000 })
}

/** Exported for unit tests (paged Search). */
export async function loadTransferStatement(
  query: TransferStatementQuery,
): Promise<TransferStatementResult> {
  return load(query)
}
